import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Phone, Wrench } from "lucide-react";

const LINES = ["Cracked glass?", "Broken stuff?", "We fix it."];

const SERVICES = [
  { n: "01", label: "Glass & Glazing" },
  { n: "02", label: "Property Maintenance" },
  { n: "03", label: "Garden Maintenance" },
];

const EASE = [0.16, 1, 0.3, 1];

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yContent = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yGhost = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0.25]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="grain relative overflow-hidden bg-navy pb-20 pt-14 text-white md:pb-28 md:pt-20"
    >
      <motion.span
        aria-hidden="true"
        style={reduce ? undefined : { y: yGhost }}
        className="text-outline-faint pointer-events-none absolute -right-6 top-6 select-none font-display text-[24vw] font-bold uppercase leading-none md:text-[18vw]"
      >
        Fixed.
      </motion.span>

      <svg
        aria-hidden="true"
        className="pointer-events-none absolute -left-10 bottom-0 h-72 w-72 opacity-[0.07] md:h-96 md:w-96"
        viewBox="0 0 200 200"
      >
        <path d="M10 190 L55 130 L40 95 L90 60 L80 20" stroke="#fff" strokeWidth="2.5" fill="none" />
        <path d="M55 130 L110 120 L150 140" stroke="#fff" strokeWidth="2" fill="none" />
        <path d="M90 60 L140 70 L185 45" stroke="#F5820A" strokeWidth="2" fill="none" />
      </svg>

      <motion.div
        style={reduce ? undefined : { y: yContent, opacity: fade }}
        className="safe-x relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_340px] lg:gap-16"
      >
        <div>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="eyebrow"
            data-testid="hero-eyebrow"
          >
            Glass &middot; Handyman &middot; Gardens &mdash; Southern Adelaide
          </motion.p>

          <h1 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-8xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-orange" : ""}`}
                  initial={reduce ? false : { y: "115%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, ease: EASE, delay: 0.2 + i * 0.14 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.7 }}
            className="mt-7 max-w-xl text-base text-white/70 md:text-lg"
            data-testid="hero-subtext"
          >
            Glass replacement, property repairs and garden maintenance across Southern Adelaide.
            One call, one visit, job sorted.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.85 }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <a href="tel:0424424600" data-testid="hero-call-button" className="btn-primary">
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call 0424 424 600
            </a>
            <button type="button" data-testid="hero-quote-button" className="btn-outline-light" onClick={onQuote}>
              <Wrench className="h-4 w-4" aria-hidden="true" />
              Get a quote
            </button>
          </motion.div>
        </div>

        <motion.aside
          initial={reduce ? false : { opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
          data-testid="hero-services-card"
          className="h-fit bg-white text-navy lg:mt-16"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 28px), calc(100% - 28px) 100%, 0 100%)" }}
        >
          <div className="border-b-2 border-navy/10 px-7 py-5">
            <p className="font-display text-sm font-semibold uppercase tracking-[0.25em] text-navy/60">
              What we fix
            </p>
          </div>
          <ul>
            {SERVICES.map((s) => (
              <li
                key={s.n}
                className="group flex items-center gap-5 border-b-2 border-navy/10 px-7 py-5 transition-colors duration-200 last:border-b-0 hover:bg-sand"
              >
                <span className="font-display text-3xl font-bold text-orange transition-transform duration-200 group-hover:-translate-y-0.5">
                  {s.n}
                </span>
                <span className="font-display text-lg font-medium uppercase tracking-wide">{s.label}</span>
              </li>
            ))}
          </ul>
          <div className="bg-navy px-7 py-4">
            <p className="text-sm text-white/70">One number covers all three.</p>
          </div>
        </motion.aside>
      </motion.div>
    </section>
  );
};
